import type { DesignerNodeData } from "../schemas/index.ts";
import { nextNumberedName, normalizedName, numberedNameBase, transformNodeSubtree } from "../shared/index.ts";

/**
 * Renumbers the authored titles in the subtree about to be imported so a copy lands as
 * `Audio 3` next to `Audio 2` rather than repeating its title. A title free at the
 * destination is kept as it is, and a title settled earlier in the same import counts
 * as taken, so a copied container and its children stay distinct from each other too.
 *
 * Best effort, not a uniqueness guarantee — an author can still type a duplicate, and
 * titles arriving by any other route (a move, a rename) are not renumbered.
 */
export const renumberClashingTitles = (node: DesignerNodeData, takenTitles: readonly string[]): DesignerNodeData => {
  const taken = new Set(takenTitles.map(normalizedName));

  return transformNodeSubtree<DesignerNodeData, DesignerNodeData>(node, (imported) => {
    const { config } = imported;
    const title = config.title;
    // a title declared multilingual is stored as `{ __ml }`, and is left alone
    if (typeof title !== "string") return imported;

    const nextTitle = taken.has(normalizedName(title)) ? nextNumberedName(numberedNameBase(title), taken) : title;
    taken.add(normalizedName(nextTitle));

    return nextTitle === title ? imported : { ...imported, config: { ...config, title: nextTitle } };
  });
};
